import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import Alert from '../components/Alert';
import Confirm from '../components/Confirm';
import Toast from '../components/Toast';
import { usuarioService } from '../services/api';

const Usuarios = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [alert, setAlert] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [toast, setToast] = useState(null);
  const [permisosTarget, setPermisosTarget] = useState(null);
  const [permisos, setPermisos] = useState(null);
  const [empresasDisp, setEmpresasDisp] = useState([]);
  const [catSuc, setCatSuc] = useState({}); // { [id_empresa]: [{...sedes}] }
  const [savingPermisos, setSavingPermisos] = useState(false);
  // Cambio de contrasena
  const [passwordTarget, setPasswordTarget] = useState(null);
  const [passwordForm, setPasswordForm] = useState({ newPassword: '', confirmPassword: '' });
  const [savingPassword, setSavingPassword] = useState(false);
  const [form, setForm] = useState({
    nombre: '',
    email: '',
    telefono: '',
    usuario: '',
    password: '',
    id_perfil: 2,
    usuario_siscont: '',
  });

  const fetchUsuarios = useCallback(async () => {
    try {
      const res = await usuarioService.getAll();
      setUsuarios(res.data);
    } catch (err) {
      setAlert({ type: 'error', message: 'Error al cargar la lista de usuarios.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsuarios();
  }, [fetchUsuarios]);

  const resetForm = () => {
    setForm({ nombre: '', email: '', telefono: '', usuario: '', password: '', id_perfil: 2, usuario_siscont: '' });
    setEditingUser(null);
  };

  const openCreate = () => {
    resetForm();
    setShowModal(true);
  };

  const openEdit = (user) => {
    setEditingUser(user);
    setForm({
      nombre: user.nombre,
      email: user.email || '',
      telefono: user.telefono || '',
      usuario: user.usuario,
      password: '',
      id_perfil: user.id_perfil,
      usuario_siscont: user.usuario_siscont || '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.nombre || !form.email) {
      setAlert({ type: 'warning', message: 'Nombre y correo son campos obligatorios.' });
      return;
    }

    if (!editingUser && !form.usuario) {
      setAlert({ type: 'warning', message: 'El usuario es obligatorio.' });
      return;
    }

    if (!editingUser && form.usuario.length > 5) {
      setAlert({ type: 'warning', message: 'El usuario no debe tener más de 5 caracteres.' });
      return;
    }

    if (!editingUser && !form.password) {
      setAlert({ type: 'warning', message: 'La contraseña es obligatoria para nuevos usuarios.' });
      return;
    }

    // Validación: usuario_siscont es opcional pero si se llena debe tener 4-5 caracteres
    const siscontTrim = (form.usuario_siscont || '').trim();
    if (siscontTrim && (siscontTrim.length < 4 || siscontTrim.length > 5)) {
      setAlert({ type: 'warning', message: 'El usuario SISCONT debe tener entre 4 y 5 caracteres.' });
      return;
    }

    try {
      if (editingUser) {
        const updateData = {
          nombre: form.nombre,
          email: form.email,
          telefono: form.telefono || null,
          id_perfil: parseInt(form.id_perfil),
          usuario_siscont: siscontTrim || null,
        };
        await usuarioService.update(editingUser.id_usuario, updateData);
        setAlert({ type: 'success', message: 'Usuario actualizado correctamente.' });
      } else {
        await usuarioService.create({
          ...form,
          id_perfil: parseInt(form.id_perfil),
          usuario_siscont: siscontTrim || null,
        });
        setAlert({ type: 'success', message: 'Usuario creado correctamente.' });
      }
      setShowModal(false);
      resetForm();
      fetchUsuarios();
    } catch (err) {
      const detail = err.response?.data?.detail;
      let msg = 'Error al guardar usuario.';
      if (typeof detail === 'string') {
        msg = detail;
      } else if (Array.isArray(detail)) {
        msg = detail.map(d => d.msg).join('. ');
      }
      setAlert({ type: 'error', message: msg });
    }
  };

  const handleDelete = (user) => {
    setConfirm({
      title: 'Eliminar usuario',
      message: `¿Está seguro de eliminar al usuario "${user.nombre}"? Esta acción no se puede deshacer.`,
      onConfirm: async () => {
        setConfirm(null);
        try {
          await usuarioService.delete(user.id_usuario);
          setToast('Usuario eliminado');
          fetchUsuarios();
        } catch (err) {
          setAlert({ type: 'error', message: 'Error al eliminar el usuario.' });
        }
      },
    });
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  /* ── Cambio de contraseña ─────────────────────────────────── */
  const abrirCambioPassword = (user) => {
    setPasswordTarget(user);
    setPasswordForm({ newPassword: '', confirmPassword: '' });
  };

  const cerrarCambioPassword = () => {
    if (savingPassword) return;
    setPasswordTarget(null);
    setPasswordForm({ newPassword: '', confirmPassword: '' });
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!passwordTarget) return;
    const { newPassword, confirmPassword } = passwordForm;
    if (!newPassword || newPassword.length < 6) {
      setAlert({ type: 'warning', message: 'La contraseña debe tener al menos 6 caracteres.' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setAlert({ type: 'warning', message: 'Las contraseñas no coinciden.' });
      return;
    }
    setSavingPassword(true);
    try {
      await usuarioService.changePassword(passwordTarget.id_usuario, newPassword);
      setToast(`Contraseña actualizada para ${passwordTarget.usuario}`);
      setPasswordTarget(null);
      setPasswordForm({ newPassword: '', confirmPassword: '' });
    } catch (err) {
      const detail = err.response?.data?.detail;
      let msg = 'Error al cambiar la contraseña.';
      if (typeof detail === 'string') msg = detail;
      else if (Array.isArray(detail)) msg = detail.map(d => d.msg).join('. ');
      setAlert({ type: 'error', message: msg });
    } finally {
      setSavingPassword(false);
    }
  };

  /* ── Permisos del usuario ─────────────────────────────────── */
  const abrirPermisos = async (user) => {
    setPermisosTarget(user);
    setPermisos(null);
    try {
      const [permRes, empresasRes] = await Promise.all([
        usuarioService.getPermisos(user.id_usuario),
        usuarioService.empresasDisponibles(),
      ]);
      const perm = permRes.data;
      const empresasPermitidasIds = new Set((perm.empresas || []).map(e => e.id_empresa));
      // Normalizar sucursales como sets por id_empresa (string key del servidor)
      const suc = {};
      Object.entries(perm.sucursales_por_empresa || {}).forEach(([k, v]) => { suc[String(k)] = new Set(v); });

      setEmpresasDisp(empresasRes.data || []);
      setPermisos({ empresas: empresasPermitidasIds, sucursales: suc });

      // Precargar catálogos de sucursales de las empresas marcadas
      const toLoad = [...empresasPermitidasIds];
      const nuevoCatSuc = { ...catSuc };
      await Promise.all(toLoad.map(async (id) => {
        if (!nuevoCatSuc[id]) {
          const r = await usuarioService.sucursalesEmpresa(id);
          nuevoCatSuc[id] = r.data || [];
        }
      }));
      setCatSuc(nuevoCatSuc);
    } catch (err) {
      setAlert({ type: 'error', message: 'No se pudieron cargar los permisos.' });
      setPermisosTarget(null);
    }
  };

  const cerrarPermisos = () => { setPermisosTarget(null); setPermisos(null); };

  const toggleEmpresaPerm = async (idEmp) => {
    setPermisos(prev => {
      const next = new Set(prev.empresas);
      const nextSuc = { ...prev.sucursales };
      if (next.has(idEmp)) {
        next.delete(idEmp);
        delete nextSuc[String(idEmp)];
      } else {
        next.add(idEmp);
        if (!nextSuc[String(idEmp)]) nextSuc[String(idEmp)] = new Set();
      }
      return { empresas: next, sucursales: nextSuc };
    });
    // cargar catálogo si falta
    if (!catSuc[idEmp]) {
      try {
        const r = await usuarioService.sucursalesEmpresa(idEmp);
        setCatSuc(prev => ({ ...prev, [idEmp]: r.data || [] }));
      } catch {}
    }
  };

  const toggleSedePerm = (idEmp, idSuc) => {
    setPermisos(prev => {
      const sucByEmp = { ...prev.sucursales };
      const s = new Set(sucByEmp[String(idEmp)] || []);
      if (s.has(idSuc)) s.delete(idSuc); else s.add(idSuc);
      sucByEmp[String(idEmp)] = s;
      return { ...prev, sucursales: sucByEmp };
    });
  };

  const marcarTodasSedesPerm = (idEmp, marcarTodas) => {
    setPermisos(prev => {
      const sucByEmp = { ...prev.sucursales };
      sucByEmp[String(idEmp)] = marcarTodas ? new Set((catSuc[idEmp] || []).map(s => s.id_sucursal)) : new Set();
      return { ...prev, sucursales: sucByEmp };
    });
  };

  const guardarPermisos = async () => {
    if (!permisosTarget || !permisos) return;
    if (permisos.empresas.size === 0) {
      setAlert({ type: 'warning', message: 'El usuario debe tener al menos una empresa asignada.' });
      return;
    }
    setSavingPermisos(true);
    try {
      const empresasArr = [...permisos.empresas];
      const sucursalesPorEmpresa = {};
      empresasArr.forEach(idEmp => {
        const s = permisos.sucursales[String(idEmp)];
        sucursalesPorEmpresa[String(idEmp)] = s ? [...s] : [];
      });
      await usuarioService.setPermisos(permisosTarget.id_usuario, {
        empresas: empresasArr,
        sucursales_por_empresa: sucursalesPorEmpresa,
      });
      setToast('Permisos actualizados');
      cerrarPermisos();
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'Error al guardar permisos.' });
    } finally { setSavingPermisos(false); }
  };

  return (
    <Layout title="Usuarios" subtitle="Gestión de usuarios del sistema">
      <div className="page-header">
        <h2>Usuarios registrados</h2>
        <button className="btn btn-primary" onClick={openCreate}>+ Nuevo Usuario</button>
      </div>

      <div className="data-table-container">
        {loading ? (
          <div className="empty-state">Cargando...</div>
        ) : usuarios.length === 0 ? (
          <div className="empty-state">No hay usuarios registrados</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Usuario</th>
                <th>Nombre</th>
                <th>Correo</th>
                <th>Teléfono</th>
                <th>SISCONT</th>
                <th>Perfil</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {usuarios.map((u) => (
                <tr key={u.id_usuario}>
                  <td style={{ fontWeight: 600 }}>{u.usuario}</td>
                  <td>{u.nombre}</td>
                  <td>{u.email || '—'}</td>
                  <td>{u.telefono || '—'}</td>
                  <td>
                    {u.usuario_siscont ? (
                      <span style={{
                        fontFamily: 'Consolas, monospace', fontWeight: 700, letterSpacing: 1,
                        padding: '2px 8px', borderRadius: 5, fontSize: 11,
                        background: 'rgba(167,139,250,0.12)', color: '#a78bfa',
                        border: '1px solid rgba(167,139,250,0.3)',
                      }}>{u.usuario_siscont}</span>
                    ) : <span style={{ color: '#475569' }}>—</span>}
                  </td>
                  <td>
                    <span className={`badge ${u.id_perfil === 1 ? 'badge-blue' : 'badge-green'}`}>
                      {u.id_perfil === 1 ? 'Admin' : 'Usuario'}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${u.activo ? 'badge-green' : 'badge-red'}`}>
                      {u.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => openEdit(u)}>Editar</button>
                      <button className="btn btn-secondary btn-sm"
                        style={{ background: 'rgba(251,191,36,0.1)', color: '#fbbf24', borderColor: 'rgba(251,191,36,0.3)' }}
                        onClick={() => abrirCambioPassword(u)}
                        title="Cambiar contraseña">
                        🔑 Contraseña
                      </button>
                      <button className="btn btn-secondary btn-sm"
                        style={{ background: 'rgba(167,139,250,0.1)', color: '#c4b5fd', borderColor: 'rgba(167,139,250,0.3)' }}
                        onClick={() => abrirPermisos(u)}>
                        🔒 Permisos
                      </button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(u)}>Eliminar</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-row">
                  <label>Nombre completo *</label>
                  <input
                    name="nombre"
                    value={form.nombre}
                    onChange={handleChange}
                    placeholder="Ej: Juan Pérez"
                  />
                </div>

                <div className="form-row">
                  <label>Correo electrónico *</label>
                  <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="correo@empresa.com"
                  />
                </div>

                <div className="form-row">
                  <label>Teléfono</label>
                  <input
                    name="telefono"
                    value={form.telefono}
                    onChange={handleChange}
                    placeholder="999 999 999"
                  />
                </div>

                {!editingUser && (
                  <>
                    <div className="form-row">
                      <label>Usuario * <span style={{ fontSize: '11px', color: 'rgba(148,163,184,0.6)' }}>(máx. 5 caracteres)</span></label>
                      <input
                        name="usuario"
                        value={form.usuario}
                        onChange={handleChange}
                        placeholder="Ej: jperez"
                        maxLength={5}
                      />
                    </div>

                    <div className="form-row">
                      <label>Contraseña *</label>
                      <input
                        name="password"
                        type="password"
                        value={form.password}
                        onChange={handleChange}
                        placeholder="Mínimo 6 caracteres"
                      />
                    </div>
                  </>
                )}

                <div className="form-row">
                  <label>Perfil</label>
                  <select name="id_perfil" value={form.id_perfil} onChange={handleChange}>
                    <option value={1}>Administrador</option>
                    <option value={2}>Usuario</option>
                  </select>
                </div>

                <div className="form-row">
                  <label>
                    Usuario SISCONT{' '}
                    <span style={{ fontSize: '11px', color: 'rgba(148,163,184,0.6)' }}>
                      (4 a 5 caracteres — opcional)
                    </span>
                  </label>
                  <input
                    name="usuario_siscont"
                    value={form.usuario_siscont}
                    onChange={e => setForm({ ...form, usuario_siscont: e.target.value.toUpperCase() })}
                    placeholder="Ej: JPZ01"
                    minLength={4}
                    maxLength={5}
                    autoComplete="off"
                    style={{ fontFamily: 'Consolas, monospace', letterSpacing: 1, textTransform: 'uppercase' }}
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingUser ? 'Guardar Cambios' : 'Crear Usuario'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {alert && (
        <Alert
          type={alert.type}
          title={alert.title}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}

      {confirm && (
        <Confirm
          title={confirm.title}
          message={confirm.message}
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
      {toast && <Toast message={toast} onClose={() => setToast(null)} />}

      {/* ═══ Modal de Cambio de Contraseña ═══ */}
      {passwordTarget && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2>Cambiar contraseña</h2>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                  Usuario: <strong>{passwordTarget.usuario}</strong> · {passwordTarget.nombre}
                </div>
              </div>
              <button className="modal-close" onClick={cerrarCambioPassword}>×</button>
            </div>
            <form onSubmit={handleChangePassword}>
              <div className="modal-body">
                <div className="form-row">
                  <label>Nueva contraseña *</label>
                  <input
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={e => setPasswordForm(p => ({ ...p, newPassword: e.target.value }))}
                    placeholder="Mínimo 6 caracteres"
                    minLength={6}
                    autoFocus
                    autoComplete="new-password"
                  />
                </div>
                <div className="form-row">
                  <label>Confirmar contraseña *</label>
                  <input
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={e => setPasswordForm(p => ({ ...p, confirmPassword: e.target.value }))}
                    placeholder="Repita la contraseña"
                    minLength={6}
                    autoComplete="new-password"
                  />
                </div>
                <div style={{
                  fontSize: 11.5, color: '#94a3b8', background: 'rgba(251,191,36,0.06)',
                  border: '1px solid rgba(251,191,36,0.2)', borderRadius: 6, padding: '8px 10px',
                }}>
                  El usuario podrá iniciar sesión inmediatamente con la nueva contraseña.
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={cerrarCambioPassword} disabled={savingPassword}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary" disabled={savingPassword}>
                  {savingPassword ? 'Guardando...' : 'Cambiar contraseña'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ═══ Modal de Permisos ═══ */}
      {permisosTarget && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 640 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2>Permisos de acceso</h2>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                  {permisosTarget.nombre} · {permisosTarget.email}
                </div>
              </div>
              <button className="modal-close" onClick={cerrarPermisos}>×</button>
            </div>
            <div className="modal-body" style={{ padding: '16px 20px', maxHeight: '70vh', overflowY: 'auto' }}>
              {!permisos ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Cargando...</div>
              ) : empresasDisp.length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', color: '#64748b' }}>No hay empresas configuradas.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>
                    Marque las <strong>empresas</strong> a las que el usuario puede acceder. Dentro de cada empresa,
                    si no se marca ninguna sede, el usuario verá <strong>todas</strong>.
                  </div>
                  {empresasDisp.map(emp => {
                    const marcada = permisos.empresas.has(emp.id_empresa);
                    const sedes = catSuc[emp.id_empresa] || [];
                    const sedesMarcadas = permisos.sucursales[String(emp.id_empresa)] || new Set();
                    const todasMarcadas = sedes.length > 0 && sedes.every(s => sedesMarcadas.has(s.id_sucursal));
                    const sinRestriccion = marcada && sedesMarcadas.size === 0;
                    return (
                      <div key={emp.id_empresa} style={{
                        border: `1px solid ${marcada ? 'rgba(96,165,250,0.3)' : 'rgba(255,255,255,0.06)'}`,
                        background: marcada ? 'rgba(96,165,250,0.05)' : 'rgba(15,27,45,0.3)',
                        borderRadius: 10, padding: '10px 12px', transition: 'all 0.15s',
                      }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                          <input type="checkbox" checked={marcada}
                            onChange={() => toggleEmpresaPerm(emp.id_empresa)}
                            style={{ width: 16, height: 16, accentColor: '#3b82f6' }} />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, fontSize: 13, color: '#e2e8f0' }}>{emp.razon_social}</div>
                            {emp.ruc && <div style={{ fontSize: 11, color: '#64748b' }}>RUC: {emp.ruc}</div>}
                          </div>
                          {marcada && (
                            <span style={{
                              fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 5,
                              background: sinRestriccion ? 'rgba(34,197,94,0.14)' : 'rgba(251,146,60,0.14)',
                              color:      sinRestriccion ? '#22c55e' : '#fb923c',
                              border: `1px solid ${sinRestriccion ? 'rgba(34,197,94,0.3)' : 'rgba(251,146,60,0.3)'}`,
                            }}>
                              {sinRestriccion ? 'Todas las sedes' : `${sedesMarcadas.size} sede(s)`}
                            </span>
                          )}
                        </label>

                        {marcada && sedes.length > 0 && (
                          <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed rgba(255,255,255,0.06)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                              <span style={{ fontSize: 10.5, color: '#64748b', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.5 }}>
                                Sedes permitidas
                              </span>
                              <div style={{ display: 'flex', gap: 6 }}>
                                <button type="button" onClick={() => marcarTodasSedesPerm(emp.id_empresa, false)}
                                  style={tinyBtnStyle(sinRestriccion)}>
                                  Sin restricción
                                </button>
                                <button type="button" onClick={() => marcarTodasSedesPerm(emp.id_empresa, true)}
                                  style={tinyBtnStyle(todasMarcadas)}>
                                  Marcar todas
                                </button>
                              </div>
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                              {sedes.map(s => {
                                const sel = sedesMarcadas.has(s.id_sucursal);
                                return (
                                  <button key={s.id_sucursal} type="button"
                                    onClick={() => toggleSedePerm(emp.id_empresa, s.id_sucursal)}
                                    style={{
                                      padding: '4px 10px', fontSize: 11.5, fontWeight: 600,
                                      borderRadius: 5, cursor: 'pointer',
                                      background: sel ? 'rgba(34,197,94,0.14)' : 'rgba(255,255,255,0.04)',
                                      color:      sel ? '#22c55e' : '#94a3b8',
                                      border: `1px solid ${sel ? 'rgba(34,197,94,0.35)' : 'rgba(255,255,255,0.08)'}`,
                                    }}>
                                    {sel ? '✓ ' : ''}{s.codigo} — {s.nombre}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={cerrarPermisos} disabled={savingPermisos}>Cancelar</button>
              <button type="button" className="btn btn-primary" onClick={guardarPermisos} disabled={savingPermisos || !permisos}>
                {savingPermisos ? 'Guardando...' : 'Guardar permisos'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

const tinyBtnStyle = (active) => ({
  padding: '2px 8px', fontSize: 10.5, fontWeight: 600, borderRadius: 4, cursor: 'pointer',
  background: active ? 'rgba(96,165,250,0.15)' : 'rgba(255,255,255,0.03)',
  color:      active ? '#93c5fd' : '#64748b',
  border: `1px solid ${active ? 'rgba(96,165,250,0.3)' : 'rgba(255,255,255,0.08)'}`,
});

export default Usuarios;
